import { cn } from "@landing/lib/utils"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"

const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md", {
  variants: {
    variant: {
      default: "-tracking-[.02rem] bg-white hover:text-blue-brand text-blue-brand",
      destructive: "bg-red-500 text-white hover:bg-red-500/90 dark:bg-red-900 dark:text-white dark:hover:bg-red-900/90",
      outline:
        "z-10 relative transition-all ease-in-out duration-300 bg-white/10 hover:bg-white/10 backdrop-blur-lg text-white overflow-hidden active:scale-[0.98] border-[0.123rem] border-white/50 hover:border-white/75 active:border-white",
      secondary: "bg-white/90 text-[#13161f] hover:bg-white/80 dark:bg-white/20 dark:text-white dark:hover:bg-white/30",
      ghost: "hover:bg-white/30 dark:hover:bg-white/30",
      link: "text-[#13161f] underline-offset-4 hover:underline dark:text-white",
    },
    size: {
      default: "h-10 px-4 py-2",
      sm: "h-9 rounded-md px-3",
      lg: "h-11 rounded-md px-8",
      icon: "h-10 w-10",
      bracket: "w-full rounded-[4.125rem] h-12.5 font-bold text-[0.98rem]",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
})

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = ({
  ref,
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps & {
  ref?: React.RefObject<HTMLButtonElement>
}) => {
  const Comp = asChild ? Slot : "button"
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
}
Button.displayName = "Button"

export { Button }
